/**
 * Credentials API (SaaS Phase A / A2). Per-provider model:
 *   GET  /providers            — selectable providers + the services each offers
 *   POST /credentials          — { provider, api_key } (one key per provider)
 *   GET  /credentials          — status-only rows { id, provider, services }
 *   DELETE /credentials/{id}
 *
 * The backend NEVER returns API keys in clear (Fernet-encrypted at rest), and a
 * single key now unlocks every service (STT / translation / TTS) the provider
 * offers — derived from the catalog, not entered per service.
 */

import { http } from '@/shared/api/http';

/** Service a provider can offer, matching the backend catalog vocabulary. */
export type ProviderService = 'stt' | 'translate' | 'tts';

/** A selectable provider from GET /providers (no key material). */
export interface ProviderInfo {
  id: string;
  label: string;
  capabilities: ProviderService[];
  key_label: string;
}

/** Status-only credential row — no key material ever crosses the wire. */
export interface CredentialStatus {
  id: string;
  provider: string;
  services: ProviderService[];
  created_at: string;
}

export interface CreateCredentialInput {
  provider: string;
  api_key: string;
}

/** Human-facing labels for services (UI badges). */
export const SERVICE_LABELS: Record<ProviderService, string> = {
  stt: 'STT',
  translate: 'Tradução',
  tts: 'TTS',
};

export const credentialsApi = {
  listProviders(): Promise<ProviderInfo[]> {
    return http.get<ProviderInfo[]>('/providers');
  },

  list(): Promise<CredentialStatus[]> {
    return http.get<CredentialStatus[]>('/credentials');
  },

  create(input: CreateCredentialInput): Promise<CredentialStatus> {
    return http.post<CredentialStatus>('/credentials', { json: input });
  },

  remove(id: string): Promise<void> {
    return http.delete<void>(`/credentials/${encodeURIComponent(id)}`);
  },
};
